import { Box } from '@mui/material';
import { TimePickerSectionProps } from './TimePickerSection.types';

/**
 * TimePickerSection - Renders scrollable time picker with hours, minutes, and optional seconds
 *
 * Features:
 * - Scrollable columns for hours (0-23), minutes (0-59), and seconds (0-59)
 * - Conditional seconds column based on includeSeconds prop
 * - Different styling for time-only vs datetime modes
 * - Active state highlighting for selected values
 *
 * @component
 * @param {TimePickerSectionProps} props - Component props
 * @returns {JSX.Element} Time picker with scrollable columns
 *
 * @example
 * <TimePickerSection
 *   hours={14}
 *   minutes={30}
 *   seconds={0}
 *   includeSeconds={false}
 *   isTimeOnly={true}
 *   onHourChange={setHours}
 *   onMinuteChange={setMinutes}
 *   onSecondChange={setSeconds}
 * />
 */
export const TimePickerSection = ({
  hours,
  minutes,
  seconds,
  includeSeconds,
  isTimeOnly,
  onHourChange,
  onMinuteChange,
  onSecondChange
}: TimePickerSectionProps) => {
  /**
   * Renders scrollable hours selector (0-23)
   *
   * @returns {JSX.Element} Hours column with buttons
   */
  const renderHours = () => (
    <div className="custom-date-picker-scroll">
      {Array.from({ length: 24 }, (_, i) => i).map(hour => (
        <button
          key={hour}
          className={`custom-date-picker-hour ${hours === hour ? 'active' : ''}`}
          onClick={e => {
            e.stopPropagation();
            onHourChange(hour);
          }}
        >
          {String(hour).padStart(2, '0')}
        </button>
      ))}
    </div>
  );

  /**
   * Renders scrollable minutes selector (0-59)
   *
   * @returns {JSX.Element} Minutes column with buttons
   */
  const renderMinutes = () => (
    <div className="custom-date-picker-scroll">
      {Array.from({ length: 60 }, (_, i) => i).map(minute => (
        <button
          key={minute}
          className={`custom-date-picker-minute ${minutes === minute ? 'active' : ''}`}
          onClick={e => {
            e.stopPropagation();
            onMinuteChange(minute);
          }}
        >
          {String(minute).padStart(2, '0')}
        </button>
      ))}
    </div>
  );

  /**
   * Renders scrollable seconds selector (0-59)
   * Only rendered when includeSeconds prop is true
   *
   * @returns {JSX.Element} Seconds column with buttons
   */
  const renderSeconds = () => (
    <div className="custom-date-picker-scroll">
      {Array.from({ length: 60 }, (_, i) => i).map(second => (
        <button
          key={second}
          className={`custom-date-picker-second ${seconds === second ? 'active' : ''}`}
          onClick={e => {
            e.stopPropagation();
            onSecondChange(second);
          }}
        >
          {String(second).padStart(2, '0')}
        </button>
      ))}
    </div>
  );

  const containerClass = isTimeOnly ? 'custom-date-picker-time-only-content' : 'custom-date-picker-time-section';
  const columnClass = isTimeOnly ? 'custom-date-picker-section' : 'custom-date-picker-time-column';

  return (
    <Box className={containerClass}>
      <Box className={columnClass}>
        <div className="custom-date-picker-label">Години</div>
        {renderHours()}
      </Box>
      <Box className={columnClass}>
        <div className="custom-date-picker-label">Хвилини</div>
        {renderMinutes()}
      </Box>
      {includeSeconds && (
        <Box className={columnClass}>
          <div className="custom-date-picker-label">Секунди</div>
          {renderSeconds()}
        </Box>
      )}
    </Box>
  );
};
